import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { MediaPlanBreadcrumbs } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlanBreadcrumbs';
import { PLAN_WIZARD_DEFAULT_VALUES } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/PlanWizard/constants';
import { getPlanWizardSchema } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/PlanWizard/schema';
import type { DigitalMediaPlan } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import type { PlanWizardForm } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import type { ArtistDetails } from '~/modules/artist-roster/types';

import { BudgetSection } from './components/BudgetSection/BudgetSection';
import { CampaignTypeSection } from './components/CampaignTypeSection/CampaignTypeSection';
import { FrequencyCapSection } from './components/FrequencyCapSection/FrequencyCapSection';
import { MarketsSection } from './components/MarketsSection/MarketsSection';
import { ObjectivesSection } from './components/ObjectivesSection/ObjectivesSection';
import { PlanDetailsSection } from './components/PlanDetailsSection/PlanDetailsSection';
import { PlatformsSection } from './components/PlatformsSection/PlatformsSection';
import { RunDatesSection } from './components/RunDatesSection/RunDatesSection';

export interface PlanWizardProps {
  artistData: ArtistDetails;
  onPlanChange?: (plan: DigitalMediaPlan) => void;
  setPlan: (plan?: DigitalMediaPlan) => void;
}

export function PlanWizard({
  artistData,
  onPlanChange,
  setPlan,
}: PlanWizardProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'digitalCampaigns',
  });

  const formMethods = useForm<PlanWizardForm>({
    resolver: zodResolver(getPlanWizardSchema(t)),
    defaultValues: PLAN_WIZARD_DEFAULT_VALUES,
  });

  const { isSubmitting, isDirty } = formMethods.formState;

  const handleSubmitPlan = useCallback(
    (formData: PlanWizardForm) => {
      // Convert the form data to a DigitalMediaPlan.
      const {
        campaignName,
        trackId,
        objectives,
        campaignType,
        markets,
        platforms,
        startDate,
        endDate,
        frequency,
        currency,
        totalBudget,
      } = formData;

      const newPlan: DigitalMediaPlan = {
        partyId: artistData.partyId,
        trackId,
        objectives,
        markets,
        totalBudget,
        platforms: platforms.map((platform) => {
          return {
            mediaPlatform: platform,
            plans: [],
          };
        }),

        // We should never need these defaults because the form validation should
        // catch any missing values. But because we want to allow unselected
        // values when the wizard first displays, we need to make these optional
        // in PlanWizardForm. Just setting defaults here to please TypeScript.
        campaignName: campaignName ?? '',
        campaignType: campaignType ?? 'preReleaseHype',
        startDate: startDate ?? new Date(),
        endDate: endDate ?? new Date(),
        frequency: frequency ?? { times: 0, period: 'day' },
        currency: currency ?? 'USD',
      };

      onPlanChange?.(newPlan);
    },
    [artistData.partyId, onPlanChange],
  );

  const onSubmit: SubmitHandler<PlanWizardForm> = handleSubmitPlan;

  const onError = useCallback((errors: unknown) => {
    // Will be updated as we add fields to the form.
    console.error('Form validation errors:', errors);
  }, []);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="flex h-full flex-col overflow-y-auto overscroll-contain bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat bg-level-01/full"
          onSubmit={formMethods.handleSubmit(onSubmit, onError)}
        >
          <div className="grow overflow-y-auto overscroll-contain px-xl py-lg">
            <MediaPlanBreadcrumbs artistData={artistData} setPlan={setPlan} />
            <div className="mb-md flex basis-full items-center">
              <h1 className="text-headline-md font-semibold">
                {t('planWizard.title')}
              </h1>
              <div className="ml-auto text-body-sm leading-tight">
                {t('planWizard.required')}
              </div>
            </div>
            <div className="flex flex-col gap-md">
              <PlanDetailsSection artistData={artistData} />
              <ObjectivesSection />
              <CampaignTypeSection />
              <MarketsSection />
              <PlatformsSection />
              <RunDatesSection />
              <FrequencyCapSection />
              <BudgetSection />
            </div>
          </div>

          <div className="flex justify-end gap-md border-t-px px-xl py-lg bg-level-03 border-t-emphasis-subtle">
            <Button
              type="submit"
              size="large"
              disabled={isSubmitting || !isDirty}
            >
              {t('planWizard.buildPlan')}
            </Button>
          </div>
        </form>
      </FormProvider>
      <DevTool control={formMethods.control} />
    </>
  );
}

PlanWizard.displayName = 'PlanWizard';
